/**
 * CompassComparison — Compass timeline + two-document directive diff.
 *
 * Phase 4's `/compass/history` returns each compass with `diff_vs_prior`
 * already computed (added/dropped/modified directive_id arrays). The
 * server-side diff is keyed by `affected_category`, so when the user picks
 * two non-adjacent cycles to compare we re-derive a fresh diff client-side
 * using the same key.
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Compass as CompassIcon,
  ArrowLeft,
  Loader2,
  Plus,
  Minus,
  Pencil,
  GitCompare,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  gardenApi,
  type CompassDirective,
  type CompassHistoryEntry,
} from '@/services/api';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useResolvedClientUid, useFetch, timeAgo } from '@/components/pdo/utils';

export function CompassComparison() {
  const clientUid = useResolvedClientUid('/admin/garden');
  const [searchParams, setSearchParams] = useSearchParams();
  const v1 = searchParams.get('v1');
  const v2 = searchParams.get('v2');

  const history = useFetch(
    () =>
      clientUid
        ? gardenApi.compassHistory(clientUid)
        : Promise.resolve({ client_uid: '', count: 0, compasses: [] }),
    [clientUid],
  );

  const compasses = history.data?.compasses ?? [];
  const a = useMemo(
    () => compasses.find((c) => c.compass_id === v1),
    [compasses, v1],
  );
  const b = useMemo(
    () => compasses.find((c) => c.compass_id === v2),
    [compasses, v2],
  );

  const select = (which: 'v1' | 'v2', id: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(which, id);
        return next;
      },
      { replace: false },
    );
  };

  const clear = () => setSearchParams(() => new URLSearchParams(), { replace: true });

  if (!clientUid) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Resolving client…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-start justify-between gap-4 px-1 pb-4 border-b border-slate-200 mb-4">
        <div className="flex items-center gap-3">
          <Link
            to={`/admin/garden/${clientUid}`}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy-900"
          >
            <ArrowLeft size={14} />
            Garden
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white shadow-glow">
              <CompassIcon size={18} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-navy-900">Compass History</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Pick two Compass documents to compare directives.
              </p>
            </div>
          </div>
        </div>
        {(v1 || v2) && (
          <button
            type="button"
            onClick={clear}
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            Clear selection
          </button>
        )}
      </header>

      {history.error && (
        <ErrorBanner error={history.error} onRetry={history.refetch} />
      )}

      <div className="grid grid-cols-12 gap-4">
        <aside className="col-span-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <header className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Timeline ({compasses.length})
          </header>
          {history.loading && compasses.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-6 text-xs text-slate-400">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : compasses.length === 0 ? (
            <EmptyState
              compact
              title="No Compass documents yet"
              hint="Run an EVALUATE garden cycle to produce a Compass."
              icon={<CompassIcon size={18} />}
            />
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
              {compasses.map((c) => {
                const inV1 = c.compass_id === v1;
                const inV2 = c.compass_id === v2;
                const diffCount =
                  (c.diff_vs_prior?.added?.length ?? 0) +
                  (c.diff_vs_prior?.dropped?.length ?? 0) +
                  (c.diff_vs_prior?.modified?.length ?? 0);
                return (
                  <li
                    key={c.compass_id}
                    className={clsx(
                      'px-3 py-2.5',
                      (inV1 || inV2) && 'bg-purple-50/60',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-mono text-xs text-navy-900 truncate">
                        {c.compass_id}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        {timeAgo(c.produced_at)}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mb-1">
                      cycle {c.cycle_number} · {c.directives.length} directives
                      {diffCount > 0 && (
                        <span className="ml-1 text-amber-700">· {diffCount} Δ</span>
                      )}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => select('v1', c.compass_id)}
                        className={clsx(
                          'flex-1 px-2 py-1 text-[10px] font-medium rounded border',
                          inV1
                            ? 'bg-purple-600 border-purple-600 text-white'
                            : 'border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-700',
                        )}
                      >
                        Set as A
                      </button>
                      <button
                        type="button"
                        onClick={() => select('v2', c.compass_id)}
                        className={clsx(
                          'flex-1 px-2 py-1 text-[10px] font-medium rounded border',
                          inV2
                            ? 'bg-purple-600 border-purple-600 text-white'
                            : 'border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-700',
                        )}
                      >
                        Set as B
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className="col-span-8">
          {a && b ? (
            <ComparisonBody a={a} b={b} />
          ) : a || b ? (
            <SingleBody compass={(a ?? b)!} />
          ) : (
            <EmptyState
              title="Pick a Compass to inspect"
              hint="Select two to compare directives side-by-side."
              icon={<GitCompare size={18} />}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function SingleBody({ compass }: { compass: CompassHistoryEntry }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <header className="px-4 py-3 border-b border-slate-200">
        <p className="font-mono text-xs text-navy-900">{compass.compass_id}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">
          {timeAgo(compass.produced_at)} · cycle {compass.cycle_number} ·{' '}
          {compass.directives.length} directives
        </p>
        {compass.summary.top_priority_category && (
          <p className="text-[11px] text-purple-700 mt-0.5">
            top priority:{' '}
            <span className="font-mono">{compass.summary.top_priority_category}</span>
          </p>
        )}
      </header>
      <ul className="divide-y divide-slate-100">
        {compass.directives.map((d) => (
          <DirectiveRow key={d.directive_id} d={d} />
        ))}
        {compass.directives.length === 0 && (
          <EmptyState compact title="No directives in this Compass" />
        )}
      </ul>
    </div>
  );
}

function DirectiveRow({
  d,
  className,
}: {
  d: CompassDirective;
  className?: string;
}) {
  return (
    <li className={clsx('px-4 py-2.5', className)}>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="font-mono text-xs text-navy-900 truncate">
          {d.directive_id}
        </span>
        <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500">
          <span>p{d.priority}</span>
          <span className="text-slate-300">·</span>
          <span className="capitalize">{d.expected_value_tier}</span>
          <span className="text-slate-300">·</span>
          <span>{d.signal_type.replace(/_/g, ' ')}</span>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mb-1">
        category:{' '}
        <span className="font-mono text-slate-700">{d.affected_category}</span>
      </p>
      {d.research_direction && (
        <p className="text-xs text-slate-700 italic">{d.research_direction}</p>
      )}
      {d.affected_concepts.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {d.affected_concepts.slice(0, 6).map((t) => (
            <span
              key={t}
              className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"
            >
              {t}
            </span>
          ))}
          {d.affected_concepts.length > 6 && (
            <span className="text-[10px] text-slate-500">
              +{d.affected_concepts.length - 6}
            </span>
          )}
        </div>
      )}
    </li>
  );
}

interface DirectiveDiff {
  added: CompassDirective[];
  dropped: CompassDirective[];
  modified: { id: string; before: CompassDirective; after: CompassDirective }[];
}

/** Match the Phase 4 server-side keying: pair directives by `affected_category`. */
function diffDirectives(
  a: CompassDirective[],
  b: CompassDirective[],
): DirectiveDiff {
  const aByCat = new Map(a.map((d) => [d.affected_category, d]));
  const bByCat = new Map(b.map((d) => [d.affected_category, d]));

  const added: CompassDirective[] = [];
  const dropped: CompassDirective[] = [];
  const modified: DirectiveDiff['modified'] = [];

  for (const [cat, before] of aByCat) {
    const after = bByCat.get(cat);
    if (!after) {
      dropped.push(before);
    } else if (
      before.priority !== after.priority ||
      before.research_direction !== after.research_direction ||
      before.signal_type !== after.signal_type
    ) {
      modified.push({ id: after.directive_id, before, after });
    }
  }
  for (const [cat, after] of bByCat) {
    if (!aByCat.has(cat)) added.push(after);
  }

  return { added, dropped, modified };
}

function ComparisonBody({
  a,
  b,
}: {
  a: CompassHistoryEntry;
  b: CompassHistoryEntry;
}) {
  const diff = useMemo(() => diffDirectives(a.directives, b.directives), [a, b]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <CompassHeader compass={a} label="A" />
        <CompassHeader compass={b} label="B" />
      </div>

      <DiffPanel
        title={`Added in B (${diff.added.length})`}
        icon={<Plus size={13} className="text-emerald-600" />}
        tone="emerald"
      >
        {diff.added.map((d) => (
          <DirectiveRow
            key={d.directive_id}
            d={d}
            className="bg-emerald-50/40"
          />
        ))}
        {diff.added.length === 0 && <EmptyState compact title="No new directives" />}
      </DiffPanel>

      <DiffPanel
        title={`Dropped (${diff.dropped.length})`}
        icon={<Minus size={13} className="text-red-600" />}
        tone="red"
      >
        {diff.dropped.map((d) => (
          <DirectiveRow
            key={d.directive_id}
            d={d}
            className="bg-red-50/40"
          />
        ))}
        {diff.dropped.length === 0 && (
          <EmptyState compact title="No directives dropped" />
        )}
      </DiffPanel>

      <DiffPanel
        title={`Modified (${diff.modified.length})`}
        icon={<Pencil size={13} className="text-amber-600" />}
        tone="amber"
      >
        {diff.modified.map(({ id, before, after }) => (
          <li key={id} className="px-4 py-2.5 bg-amber-50/40">
            <p className="font-mono text-xs text-navy-900 mb-1">{id}</p>
            <p className="text-[11px] text-slate-500 mb-1">
              category:{' '}
              <span className="font-mono">{after.affected_category}</span>
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  Before
                </p>
                <p className="font-mono text-slate-600">
                  p{before.priority} · {before.signal_type}
                </p>
                <p className="text-[11px] text-slate-500 italic mt-0.5 line-clamp-2">
                  {before.research_direction}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  After
                </p>
                <p className="font-mono text-amber-700">
                  p{after.priority} · {after.signal_type}
                </p>
                <p className="text-[11px] text-amber-700/80 italic mt-0.5 line-clamp-2">
                  {after.research_direction}
                </p>
              </div>
            </div>
          </li>
        ))}
        {diff.modified.length === 0 && (
          <EmptyState compact title="No directives changed" />
        )}
      </DiffPanel>
    </div>
  );
}

function CompassHeader({
  compass,
  label,
}: {
  compass: CompassHistoryEntry;
  label: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </p>
      <p className="font-mono text-xs text-navy-900">{compass.compass_id}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">
        {timeAgo(compass.produced_at)} · cycle {compass.cycle_number} ·{' '}
        {compass.directives.length} directives
      </p>
    </div>
  );
}

function DiffPanel({
  title,
  icon,
  tone,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  tone: 'emerald' | 'red' | 'amber';
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <header
        className={clsx(
          'flex items-center gap-2 px-4 py-2.5 border-b border-slate-200',
          tone === 'emerald' && 'bg-emerald-50/50',
          tone === 'red' && 'bg-red-50/50',
          tone === 'amber' && 'bg-amber-50/50',
        )}
      >
        {icon}
        <h3 className="text-sm font-semibold text-navy-900">{title}</h3>
      </header>
      <ul className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
        {children}
      </ul>
    </section>
  );
}
